import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import axios from 'axios';
import ParentLinkRequests from '../components/parent/ParentLinkRequests';
import OwnerPageLayout from '../components/layout/OwnerPageLayout';
import ActiveSchoolSwitcher from '../components/ActiveSchoolSwitcher';
import WardSwitcher from '../components/WardSwitcher';
import { getActiveWardStudentId, setActiveWardStudentId } from '../utils/wardContext';
import { getActiveSchool, setActiveSchool } from '../utils/activeSchool';
import SchoolAvatar from '../components/SchoolAvatar';
import { groupSubscriptions, getGroupedLessonsSummary } from '../utils/groupSubscriptions';
import { useOnboardingPageStep } from '../onboarding/useOnboardingPageStep';
import { useTranslation } from 'react-i18next';
import { formatDate, formatDateTime } from '../utils/dateFormat';
import { getPaymentMethodDisplay } from '../utils/paymentMethodDisplay';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5037/api';

const TX_STYLE = {
    deposit: { icon: '💰', color: '#2e7d32', bg: '#e8f5e9' },
    subscription: { icon: '🎫', color: '#c62828', bg: '#fce4ec' },
    lesson: { icon: '📅', color: '#e65100', bg: '#fff3e0' },
    rent: { icon: '🛼', color: '#7b1fa2', bg: '#f3e5f5' },
    card_rent: { icon: '💳', color: '#7b1fa2', bg: '#f3e5f5' },
    purchase: { icon: '🛒', color: '#EB3333', bg: '#e3f2fd' },
    refund: { icon: '↩️', color: '#2e7d32', bg: '#e8f5e9' },
};

const QUICK_AMOUNTS = [500, 1000, 2000, 5000];

const PAYMENT_NOT_CONFIGURED_RE = /not configured|\u043d\u0435 \u043d\u0430\u0441\u0442\u0440\u043e\u0435\u043d\u0430/i;

function isPaymentNotConfiguredMessage(msg) {
    if (!msg || typeof msg !== 'string') return false;
    return PAYMENT_NOT_CONFIGURED_RE.test(msg);
}

export default function MyBalancePage() {
    const { t } = useTranslation(['balance', 'common', 'nav', 'messages', 'errors', 'payment']);
    const TRANSACTION_ICONS = useMemo(() => Object.fromEntries(
        Object.entries(TX_STYLE).map(([key, style]) => [
            key,
            { ...style, label: t(`balance:tx.${key}`, { defaultValue: key }) }
        ])
    ), [t]);
    useOnboardingPageStep('view_balance', ['Member']);
    const [loading, setLoading] = useState(true);
    const [balance, setBalance] = useState(null);
    const [subs, setSubs] = useState([]);
    const [history, setHistory] = useState([]);
    const [studentId, setStudentId] = useState(null);
    const [schoolId, setSchoolId] = useState(null);
    const [walletScope, setWalletScope] = useState(null);
    const [schoolName, setSchoolName] = useState('');
    const [schoolLogo, setSchoolLogo] = useState(null);
    const [tab, setTab] = useState('balance');
    const [payAmount, setPayAmount] = useState('');
    const [payLoading, setPayLoading] = useState(false);
    const [payError, setPayError] = useState(null);
    const [payNotConfigured, setPayNotConfigured] = useState(false);
    const [plans, setPlans] = useState([]);
    const [buyingPlanId, setBuyingPlanId] = useState(null);
    const [buyError, setBuyError] = useState(null);
    const [wards, setWards] = useState([]);
    const [selfStudentId, setSelfStudentId] = useState(null);

    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('userId');
    const api = axios.create({
        baseURL: API,
        headers: { Authorization: `Bearer ${token}` }
    });

    const loadStartedRef = useRef(false);

    const applyProfile = useCallback((profile) => {
        if (!profile) return;
        setSchoolId(profile.schoolId);
        setStudentId(profile.id);
        setSchoolName(profile.schoolName || '');
        setSchoolLogo(profile.schoolLogo || null);
        setWalletScope(profile.walletScope || null);
        setActiveSchool({
            schoolId: profile.schoolId,
            schoolName: profile.schoolName,
            schoolLogo: profile.schoolLogo,
            accessRole: 'member',
            studentId: profile.id
        });
    }, []);

    const loadSchoolData = useCallback(async (profile, options = {}) => {
        const { syncPayments = false, wardStudentId } = options;
        if (!profile?.schoolId || !profile?.id) return;

        const wardParam = wardStudentId && wardStudentId !== profile.id
            ? `&wardStudentId=${wardStudentId}`
            : '';
        const targetStudentId = wardStudentId || profile.id;

        const [balRes, subsRes, plansRes] = await Promise.all([
            api.get(`/subscription/balance?schoolId=${profile.schoolId}${wardParam}`),
            api.get(`/subscription?studentId=${targetStudentId}`),
            api.get(`/subscriptionplan/catalog?schoolId=${profile.schoolId}`)
        ]);

        setBalance(balRes.data.balance);
        setHistory(balRes.data.history || []);
        setSubs(subsRes.data || []);
        setPlans(plansRes.data || []);
        if (balRes.data.walletScope) setWalletScope(balRes.data.walletScope);
        if (balRes.data.studentId) {
            setStudentId(balRes.data.studentId);
        } else {
            setStudentId(targetStudentId);
        }

        if (balRes.data.balance < 0) {
            setPayAmount(String(Math.ceil(Math.abs(balRes.data.balance))));
        }

        if (syncPayments) {
            try {
                await syncPendingOnce(profile.schoolId);
                const balAgain = await api.get(`/subscription/balance?schoolId=${profile.schoolId}`);
                setBalance(balAgain.data.balance);
                setHistory(balAgain.data.history || []);
                if (balAgain.data.walletScope) setWalletScope(balAgain.data.walletScope);
            } catch (syncErr) {
                console.warn('sync-pending', syncErr);
            }
        }
    }, []);

    useEffect(() => {
        if (loadStartedRef.current) return;
        loadStartedRef.current = true;
        loadData();
    }, []);

    async function syncPendingOnce(schoolIdForSync) {
        const key = `school-payment-sync-${schoolIdForSync}`;
        const last = sessionStorage.getItem(key);
        if (last && Date.now() - Number(last) < 90_000) return false;

        const syncRes = await api.post(`/schools/${schoolIdForSync}/payments/sync-pending`);
        sessionStorage.setItem(key, String(Date.now()));
        return syncRes.data?.syncedCount > 0;
    }

    async function reloadProfile() {
        if (!schoolId || !studentId) {
            await loadData();
            return;
        }
        try {
            await loadSchoolData({
                id: studentId,
                schoolId,
                schoolName,
                walletScope
            });
        } catch (err) {
            console.error(err);
        }
    }

    async function handleSchoolChange(profile) {
        if (!profile) return;
        setLoading(true);
        applyProfile(profile);
        try {
            await loadSchoolData(profile);
        } catch (err) {
            console.error(err);
        }
        setLoading(false);
    }

    useEffect(() => {
        const onSchoolChanged = (e) => {
            const d = e.detail;
            const profile = d?.profile || (d?.schoolId ? {
                schoolId: d.schoolId,
                id: d.profile?.id ?? d.studentId,
                schoolName: d.profile?.schoolName,
                schoolLogo: d.profile?.schoolLogo,
                walletScope: d.profile?.walletScope
            } : null);
            if (!profile?.schoolId) return;
            const fromMe = { ...profile, id: profile.id ?? profile.studentId };
            handleSchoolChange(fromMe);
        };
        window.addEventListener('active-school-changed', onSchoolChanged);
        window.addEventListener('member-school-changed', onSchoolChanged);
        return () => {
            window.removeEventListener('active-school-changed', onSchoolChanged);
            window.removeEventListener('member-school-changed', onSchoolChanged);
        };
    });

    async function loadData() {
        try {
            const profilesRes = await api.get('/student/me');
            const body = profilesRes.data || {};
            const profiles = body.profiles ?? (Array.isArray(body) ? body : []);
            const wardList = body.wards ?? [];
            setWards(wardList);

            if (profiles.length === 0 && wardList.length === 0) {
                setLoading(false);
                return;
            }

            const active = getActiveSchool();
            let profile = active
                ? profiles.find(p => p.schoolId === active.schoolId)
                : null;
            if (!profile && active?.studentId) {
                profile = profiles.find(p => p.id === active.studentId);
            }
            if (!profile) profile = profiles[0];

            let wardId = getActiveWardStudentId();
            if (!profile && wardList.length > 0) {
                const w = wardList.find(x => x.schoolId === active?.schoolId) || wardList[0];
                wardId = w.studentId;
                setActiveWardStudentId(wardId);
                profile = {
                    id: w.studentId,
                    schoolId: w.schoolId,
                    schoolName: w.schoolName,
                    schoolLogo: w.schoolLogo,
                    fullName: w.studentFullName
                };
            }

            const selfId = profiles[0]?.id ?? null;
            setSelfStudentId(selfId);
            applyProfile(profile);
            await loadSchoolData(profile, {
                syncPayments: true,
                wardStudentId: wardId && wardId !== profile.id ? wardId : undefined
            });
        } catch (err) {
            console.error(err);
        }
        setLoading(false);
    }

    async function handleBuyWithBalance(plan) {
        setBuyError(null);
        setBuyingPlanId(plan.id);
        try {
            await api.post('/subscription/purchase', {
                subscriptionPlanId: plan.id,
                studentId
            });
            await reloadProfile();
            setTab('subs');
            alert(t('balance:planPurchased', { name: plan.name }));
        } catch (err) {
            const d = err.response?.data;
            setBuyError(d?.message || d?.error || t('balance:buyFailed'));
        }
        setBuyingPlanId(null);
    }

    async function handleBuyPlanWithCard(plan) {
        setBuyError(null);
        const price = plan.currentPrice;
        if (!price || price <= 0) {
            setBuyError(t('balance:planNoPrice'));
            return;
        }
        setBuyingPlanId(plan.id);
        try {
            const res = await api.post(`/schools/${schoolId}/payments/create`, {
                studentId,
                amount: price,
                purpose: 'subscription',
                purposeId: plan.id
            });
            if (res.data?.paymentAvailable === false) {
                const contact = res.data?.contactInfo;
                setBuyError(t('payment:notConfiguredDesc', {
                    name: contact?.ownerName || contact?.schoolName || t('payment:school')
                }));
                setBuyingPlanId(null);
                return;
            }
            const url = res.data?.confirmationUrl;
            if (!url) {
                setBuyError(t('balance:payLinkMissing'));
                setBuyingPlanId(null);
                return;
            }
            window.location.href = url;
        } catch (err) {
            setBuyError(err.response?.data?.message || t('balance:createPaymentFailed'));
            setBuyingPlanId(null);
        }
    }

    async function handlePayByCard(e) {
        e.preventDefault();
        setPayError(null);
        setPayNotConfigured(false);

        const amount = parseFloat(String(payAmount).replace(',', '.'));
        if (!amount || amount <= 0) {
            setPayError(t('balance:amountRequired'));
            return;
        }
        if (!schoolId || !studentId) {
            setPayError(t('balance:studentNotLoaded'));
            return;
        }

        setPayLoading(true);
        try {
            const res = await api.post(`/schools/${schoolId}/payments/create`, {
                studentId,
                amount,
                purpose: 'deposit'
            });
            if (res.data?.paymentAvailable === false) {
                const contact = res.data?.contactInfo;
                setPayNotConfigured(true);
                setPayError(t('payment:notConfiguredDesc', {
                    name: contact?.ownerName || contact?.schoolName || t('payment:school')
                }));
                setPayLoading(false);
                return;
            }
            const url = res.data?.confirmationUrl;
            if (!url) {
                setPayError(t('balance:payLinkMissing'));
                setPayLoading(false);
                return;
            }
            window.location.href = url;
        } catch (err) {
            const msg = err.response?.data?.message
                || err.response?.data?.title
                || t('balance:createPaymentFailed');
            setPayNotConfigured(isPaymentNotConfiguredMessage(msg));
            setPayError(isPaymentNotConfiguredMessage(msg) ? t('balance:payNotConfigured') : msg);
            setPayLoading(false);
        }
    }

    const groupedSubs = useMemo(() => groupSubscriptions(subs), [subs]);
    const lessonsSummary = useMemo(() => getGroupedLessonsSummary(groupedSubs), [groupedSubs]);
    const totalLessons = lessonsSummary.lessonsLeft;
    const activeSubsCount = lessonsSummary.activeGroups;

    const pageTabs = useMemo(() => [
        { key: 'balance', label: t('balance:tabBalance') },
        { key: 'subs', label: t('balance:tabSubs') },
        { key: 'history', label: t('balance:tabHistory') },
    ], [t]);

    if (loading) return (
        <OwnerPageLayout title={t('balance:title')} maxWidth={860}>
            <div className="sp-loading-page">{t('balance:loading')}</div>
        </OwnerPageLayout>
    );

    if (!studentId && wards.length === 0) return (
        <OwnerPageLayout title={t('balance:title')} maxWidth={860}>
            <div style={{ textAlign: 'center', padding: '60px' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>🏫</div>
                <div style={{ fontSize: '18px', color: '#555', marginBottom: '8px' }}>
                    {t('balance:noSchool')}
                </div>
                <a href="/apply" style={{
                    display: 'inline-block', marginTop: '16px',
                    padding: '12px 24px', background: '#EB3333',
                    color: 'white', borderRadius: '10px', textDecoration: 'none',
                    fontWeight: '600'
                }}>
                    {t('balance:applySchool')}
                </a>
            </div>
        </OwnerPageLayout>
    );

    return (
        <OwnerPageLayout
            title={t('balance:title')}
            subtitle={schoolName || undefined}
            maxWidth={860}
        >
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12 }}>
                    <SchoolAvatar logoPath={schoolLogo} size={52} alt={schoolName} />
                </div>
                <ActiveSchoolSwitcher />

                <WardSwitcher
                    wards={wards}
                    selfStudentId={selfStudentId}
                    schoolId={schoolId}
                    onChange={(wardId) => {
                        const base = selfStudentId
                            ? { id: selfStudentId, schoolId, schoolName, schoolLogo }
                            : { id: wardId, schoolId, schoolName, schoolLogo };
                        loadSchoolData(
                            { ...base, id: wardId === selfStudentId ? selfStudentId : wardId },
                            { wardStudentId: wardId !== selfStudentId ? wardId : undefined }
                        );
                    }}
                />

                <ParentLinkRequests />

                <p style={{ margin: '12px 0 0', fontSize: '14px' }}>
                    <a href="/rental" style={{ color: '#EB3333', fontWeight: '600', textDecoration: 'none' }}>
                        {t('balance:rentalLink')}
                    </a>
                </p>

                {walletScope?.isSharedWallet && (
                    <div style={{
                        background: '#f3eeff', border: '1px solid #d4c4f7',
                        borderRadius: '12px', padding: '12px 16px',
                        marginBottom: '16px', fontSize: '13px', color: '#4a2d8f'
                    }}>
                        {t('balance:sharedWallet', { names: walletScope.schoolNames?.join(', ') })}
                        {walletScope.yooKassaShopIdMasked && (
                            <span style={{ display: 'block', marginTop: '4px', color: '#EB3333' }}>
                                {t('balance:yookassaAccount', { id: walletScope.yooKassaShopIdMasked })}
                            </span>
                        )}
                    </div>
                )}

                {/* Карточки */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                    <div style={{
                        background: balance >= 0
                            ? 'linear-gradient(135deg, #1a7f2e, #2ecc71)'
                            : 'linear-gradient(135deg, #c62828, #e74c3c)',
                        borderRadius: '16px', padding: '24px', color: 'white'
                    }}>
                        <div style={{ fontSize: '13px', opacity: 0.8, marginBottom: '8px' }}>
                            {t('balance:moneyBalance')}
                        </div>
                        <div style={{ fontSize: '36px', fontWeight: '800', marginBottom: '6px' }}>
                            {balance >= 0 ? '+' : ''}{(balance || 0).toLocaleString('ru-RU')} ₽
                        </div>
                        <div style={{ fontSize: '12px', opacity: 0.7 }}>
                            {balance >= 0 ? t('balance:available') : t('balance:needTopUp')}
                        </div>
                    </div>

                    <div style={{
                        background: 'linear-gradient(135deg, #EB3333, #9b59b6)',
                        borderRadius: '16px', padding: '24px', color: 'white'
                    }}>
                        <div style={{ fontSize: '13px', opacity: 0.8, marginBottom: '8px' }}>
                            {t('balance:lessonsLeftTitle')}
                        </div>
                        <div style={{ fontSize: '36px', fontWeight: '800', marginBottom: '6px' }}>
                            {totalLessons}
                        </div>
                        <div style={{ fontSize: '12px', opacity: 0.7 }}>
                            {activeSubsCount > 0
                                ? t('balance:subsWithLessons', { count: activeSubsCount })
                                : t('balance:noActiveSubs')
                            }
                        </div>
                    </div>
                </div>

                {/* Предупреждения */}
                {balance < 0 && (
                    <div style={{
                        background: '#ffebee', border: '1px solid #ef9a9a',
                        borderRadius: '12px', padding: '14px 16px',
                        marginBottom: '16px', display: 'flex', gap: '12px', alignItems: 'center'
                    }}>
                        <span style={{ fontSize: '24px' }}>💳</span>
                        <div>
                            <div style={{ fontWeight: '700', color: '#c62828' }}>
                                {t('balance:negativeBalance')}
                            </div>
                            <div style={{ fontSize: '13px', color: '#b71c1c' }}>
                                {t('balance:payDebt', { amount: Math.abs(balance).toLocaleString('ru-RU') })}
                            </div>
                        </div>
                    </div>
                )}

                {totalLessons <= 2 && totalLessons > 0 && (
                    <div style={{
                        background: '#fff3e0', border: '1px solid #ffcc02',
                        borderRadius: '12px', padding: '14px 16px',
                        marginBottom: '16px', display: 'flex', gap: '12px', alignItems: 'center'
                    }}>
                        <span style={{ fontSize: '24px' }}>⚠️</span>
                        <div>
                            <div style={{ fontWeight: '700', color: '#e65100' }}>
                                {t('balance:lessonsRunningOut')}
                            </div>
                            <div style={{ fontSize: '13px', color: '#bf360c' }}>
                                {t('balance:lessonsLowHint', { count: totalLessons })}
                            </div>
                        </div>
                    </div>
                )}

                {/* Вкладки */}
                <div style={{
                    display: 'flex', borderBottom: '2px solid var(--sp-gray-100)',
                    marginBottom: '20px'
                }}>
                    {pageTabs.map(tabItem => (
                        <button key={tabItem.key} onClick={() => setTab(tabItem.key)} style={{
                            padding: '12px 20px', border: 'none', background: 'none',
                            cursor: 'pointer', fontSize: '14px',
                            fontWeight: tab === tabItem.key ? '700' : '400',
                            color: tab === tabItem.key ? '#1A1A1A' : '#888',
                            borderBottom: `2px solid ${tab === tabItem.key ? '#EB3333' : 'transparent'}`,
                            marginBottom: '-2px'
                        }}>
                            {tabItem.label}
                        </button>
                    ))}
                </div>

                {/* ── БАЛАНС ── */}
                {tab === 'balance' && (
                    <div style={{
                        background: 'white', borderRadius: '14px',
                        padding: '20px', border: '1px solid var(--sp-gray-100)'
                    }}>
                        <h3 style={{ margin: '0 0 16px', fontSize: '16px' }}>{t('balance:summary')}</h3>
                        {(() => {
                            const totalPurchased = groupedSubs.reduce((n, g) => n + g.purchaseCount, 0);
                            const rows = [
                                { label: t('balance:rowMoney'), value: `${(balance || 0).toLocaleString('ru-RU')} ₽`, color: balance >= 0 ? '#2e7d32' : '#c62828' },
                                { label: t('balance:rowSubTypes'), value: groupedSubs.length, color: '#EB3333' },
                                ...(lessonsSummary.lessonsPurchased > 0 ? [{
                                    label: t('balance:rowLessonsUsed'),
                                    value: t('balance:lessonsUsedOf', { used: lessonsSummary.lessonsUsed, total: lessonsSummary.lessonsPurchased }),
                                    color: '#e65100'
                                }] : []),
                                { label: t('balance:rowLessonsLeft'), value: totalLessons, color: '#EB3333' },
                                { label: t('balance:rowPurchased'), value: totalPurchased, color: '#555' },
                            ];
                            return rows.map((row, i) => (
                            <div key={row.label} style={{
                                display: 'flex', justifyContent: 'space-between',
                                alignItems: 'center', padding: '12px 0',
                                borderBottom: i < rows.length - 1 ? '1px solid #f5f5f5' : 'none'
                            }}>
                                <span style={{ fontSize: '14px', color: '#555' }}>{row.label}</span>
                                <span style={{ fontSize: '16px', fontWeight: '700', color: row.color }}>
                                    {row.value}
                                </span>
                            </div>
                            ));
                        })()}

                        <div style={{
                            marginTop: '24px', paddingTop: '20px',
                            borderTop: '1px solid var(--sp-gray-100)'
                        }}>
                            <h3 style={{ margin: '0 0 12px', fontSize: '16px' }}>
                                {t('balance:topUpCardTitle')}
                            </h3>
                            <p style={{ margin: '0 0 14px', fontSize: '13px', color: '#888' }}>
                                {t('balance:topUpCardHint')}
                            </p>

                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                                {QUICK_AMOUNTS.map(a => (
                                    <button
                                        key={a}
                                        type="button"
                                        onClick={() => setPayAmount(String(a))}
                                        style={{
                                            padding: '8px 14px', borderRadius: '8px',
                                            border: payAmount === String(a) ? '2px solid #EB3333' : '1px solid #ddd',
                                            background: payAmount === String(a) ? '#f3eeff' : 'white',
                                            cursor: 'pointer', fontSize: '13px', fontWeight: '600'
                                        }}
                                    >
                                        {a.toLocaleString('ru-RU')} ₽
                                    </button>
                                ))}
                            </div>

                            <form onSubmit={handlePayByCard}>
                                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                                    <div style={{ flex: '1 1 140px' }}>
                                        <label style={{ fontSize: '12px', color: '#888', display: 'block', marginBottom: '6px' }}>
                                            {t('balance:amountLabel')}
                                        </label>
                                        <input
                                            type="number"
                                            min="1"
                                            step="0.01"
                                            value={payAmount}
                                            onChange={e => setPayAmount(e.target.value)}
                                            placeholder="1000"
                                            required
                                            style={{
                                                width: '100%', padding: '12px 14px', fontSize: '16px',
                                                border: '1px solid #ddd', borderRadius: '10px', boxSizing: 'border-box'
                                            }}
                                        />
                                    </div>
                                    <button
                                        type="submit"
                                        disabled={payLoading}
                                        style={{
                                            flex: '0 0 auto', padding: '12px 24px',
                                            background: payLoading ? '#aaa' : 'linear-gradient(135deg, #EB3333, #9b59b6)',
                                            color: 'white', border: 'none', borderRadius: '10px',
                                            fontWeight: '700', fontSize: '15px', cursor: payLoading ? 'wait' : 'pointer'
                                        }}
                                    >
                                        {payLoading ? t('balance:redirecting') : t('balance:payByCard')}
                                    </button>
                                </div>
                            </form>

                            {payError && (
                                <div style={{
                                    marginTop: '12px', padding: '12px 14px',
                                    background: '#ffebee', borderRadius: '10px',
                                    fontSize: '13px', color: '#c62828'
                                }}>
                                    {payError}
                                    {payNotConfigured && (
                                        <span>{t('balance:payNotConfiguredHint')}</span>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* ── ТАРИФЫ И АБОНЕМЕНТЫ ── */}
                {tab === 'subs' && (
                    <div>
                        <div style={{
                            background: 'white', borderRadius: '14px',
                            padding: '20px', border: '1px solid var(--sp-gray-100)',
                            marginBottom: '16px'
                        }}>
                            <h3 style={{ margin: '0 0 8px', fontSize: '16px' }}>{t('balance:schoolPlans')}</h3>
                            <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#888' }}>
                                {t('balance:schoolPlansHint')}
                            </p>
                            {buyError && (
                                <div style={{
                                    marginBottom: '12px', padding: '12px', background: '#ffebee',
                                    borderRadius: '10px', fontSize: '13px', color: '#c62828'
                                }}>
                                    {buyError}
                                </div>
                            )}
                            {plans.length === 0 ? (
                                <div style={{ color: '#aaa', fontSize: '14px' }}>{t('balance:noPlans')}</div>
                            ) : plans.map(plan => {
                                const price = plan.currentPrice ?? 0;
                                const canPayFromBalance = balance != null && balance >= price;
                                const busy = buyingPlanId === plan.id;
                                return (
                                    <div key={plan.id} style={{
                                        border: '1px solid var(--sp-gray-100)', borderRadius: '12px',
                                        padding: '16px', marginBottom: '10px'
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
                                            <div>
                                                <div style={{ fontWeight: '700', fontSize: '16px' }}>{plan.name}</div>
                                                {plan.description && (
                                                    <div style={{ fontSize: '13px', color: '#666', marginTop: '4px' }}>{plan.description}</div>
                                                )}
                                                <div style={{ fontSize: '12px', color: '#888', marginTop: '6px' }}>
                                                    {plan.lessonsCount != null
                                                        ? t('balance:lessonsCount', { count: plan.lessonsCount })
                                                        : t('balance:unlimited')}
                                                    {plan.validDays ? t('balance:validDays', { days: plan.validDays }) : ''}
                                                </div>
                                            </div>
                                            <div style={{ fontSize: '22px', fontWeight: '800', color: '#EB3333' }}>
                                                {price.toLocaleString('ru-RU')} ₽
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                            <button
                                                type="button"
                                                disabled={busy || payLoading}
                                                onClick={() => handleBuyPlanWithCard(plan)}
                                                style={{
                                                    padding: '10px 16px', borderRadius: '10px', border: 'none',
                                                    background: busy ? '#aaa' : '#EB3333', color: 'white',
                                                    fontWeight: '600', fontSize: '13px', cursor: busy ? 'wait' : 'pointer'
                                                }}
                                            >
                                                {t('balance:payPlanCard')}
                                            </button>
                                            <button
                                                type="button"
                                                disabled={busy || !canPayFromBalance}
                                                onClick={() => handleBuyWithBalance(plan)}
                                                title={!canPayFromBalance ? t('balance:needBalance', { price }) : ''}
                                                style={{
                                                    padding: '10px 16px', borderRadius: '10px',
                                                    border: '1px solid #2e7d32',
                                                    background: canPayFromBalance ? 'white' : '#f5f5f5',
                                                    color: canPayFromBalance ? '#2e7d32' : '#aaa',
                                                    fontWeight: '600', fontSize: '13px',
                                                    cursor: canPayFromBalance && !busy ? 'pointer' : 'not-allowed'
                                                }}
                                            >
                                                {t('balance:payFromBalance', { balance: balance?.toLocaleString('ru-RU') ?? 0 })}
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <h3 style={{ margin: '0 0 12px', fontSize: '16px', color: '#1A1A1A' }}>{t('balance:mySubs')}</h3>
                        {groupedSubs.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '48px', background: 'white', borderRadius: '14px' }}>
                                <div style={{ fontSize: '40px', marginBottom: '12px' }}>🎫</div>
                                <div style={{ color: '#aaa' }}>{t('balance:noSubs')}</div>
                            </div>
                        ) : groupedSubs.map(g => {
                            const hasPack = g.hasLessonPack && !g.isUnlimited;
                            const pct = hasPack && g.totalLessonsPurchased > 0
                                ? (g.lessonsLeft / g.totalLessonsPurchased) * 100
                                : 0;
                            const showActive = g.hasActive && (g.isUnlimited || g.lessonsLeft > 0);

                            return (
                                <div key={g.key} style={{
                                    background: 'white', borderRadius: '14px',
                                    padding: '18px', marginBottom: '10px',
                                    border: `1px solid ${showActive ? '#c8e6c9' : '#eee'}`,
                                    opacity: showActive || g.lessonsUsed > 0 ? 1 : 0.65
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '4px' }}>
                                                <span style={{ fontWeight: '700', fontSize: '16px' }}>{g.name}</span>
                                                {g.purchaseCount > 1 && (
                                                    <span style={{
                                                        background: '#f3eeff', color: '#EB3333',
                                                        padding: '2px 10px', borderRadius: '10px',
                                                        fontSize: '11px', fontWeight: '700'
                                                    }}>
                                                        {t('balance:purchasedTimes', { count: g.purchaseCount })}
                                                    </span>
                                                )}
                                            </div>
                                            <div style={{ fontSize: '12px', color: '#888' }}>
                                                {hasPack && (
                                                    <span>
                                                        {t('balance:lessonsUsedLine', { used: g.lessonsUsed, total: g.totalLessonsPurchased })}
                                                    </span>
                                                )}
                                                {g.isUnlimited && (
                                                    <span>{t('balance:unlimitedPurchases', { count: g.purchaseCount })}</span>
                                                )}
                                            </div>
                                            <div style={{ fontSize: '12px', color: '#aaa', marginTop: '4px' }}>
                                                {t('balance:firstPurchase', { date: formatDate(g.firstPurchaseDate) })}
                                                {g.latestExpiryDate && t('balance:expiryUntil', { date: formatDate(g.latestExpiryDate) })}
                                            </div>
                                        </div>
                                        <div style={{ textAlign: 'right', marginLeft: '12px' }}>
                                            {hasPack ? (
                                                <>
                                                    <div style={{
                                                        fontSize: '28px', fontWeight: '800',
                                                        color: g.lessonsLeft > 2 ? '#2e7d32' : '#e74c3c'
                                                    }}>
                                                        {g.lessonsLeft} / {g.totalLessonsPurchased}
                                                    </div>
                                                    <div style={{ fontSize: '12px', color: '#aaa' }}>{t('balance:leftLabel')}</div>
                                                </>
                                            ) : (
                                                <div style={{ fontSize: '24px' }}>♾️</div>
                                            )}
                                        </div>
                                    </div>

                                    {hasPack && (
                                        <div style={{ height: '6px', background: '#f0f0f0', borderRadius: '3px', marginBottom: '10px' }}>
                                            <div style={{
                                                height: '100%', borderRadius: '3px',
                                                width: `${pct}%`,
                                                background: pct > 30 ? '#2ecc71' : '#e74c3c',
                                                transition: 'width 0.3s'
                                            }} />
                                        </div>
                                    )}

                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                                        <span style={{
                                            background: showActive ? '#e8f5e9' : '#f5f5f5',
                                            color: showActive ? '#2e7d32' : '#888',
                                            padding: '4px 12px', borderRadius: '10px',
                                            fontSize: '12px', fontWeight: '700'
                                        }}>
                                            {showActive
                                                ? (g.activeCount > 1
                                                    ? t('balance:subActiveCount', { count: g.activeCount })
                                                    : t('balance:subActive'))
                                                : g.lessonsLeft === 0 && hasPack
                                                    ? t('balance:subUsed')
                                                    : t('balance:subEnded')}
                                        </span>
                                        <span style={{ fontSize: '13px', color: '#888' }}>
                                            {t('balance:paidTotal', { amount: g.pricePaidTotal.toLocaleString('ru-RU') })}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* ── ИСТОРИЯ ── */}
                {tab === 'history' && (
                    <div style={{
                        background: 'white', borderRadius: '14px',
                        padding: '20px', border: '1px solid var(--sp-gray-100)'
                    }}>
                        {history.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '32px', color: '#aaa' }}>
                                <div style={{ fontSize: '36px', marginBottom: '10px' }}>📋</div>
                                {t('balance:noHistory')}
                            </div>
                        ) : history.map(tx => {
                            const conf = TRANSACTION_ICONS[tx.type] || TRANSACTION_ICONS.deposit;
                            const pay = getPaymentMethodDisplay(tx.paymentMethod, t);
                            return (
                                <div key={tx.id} style={{
                                    display: 'flex', alignItems: 'center', gap: '12px',
                                    padding: '12px 0', borderBottom: '1px solid #f5f5f5'
                                }}>
                                    <div style={{
                                        width: '42px', height: '42px', borderRadius: '50%',
                                        background: conf.bg, display: 'flex',
                                        alignItems: 'center', justifyContent: 'center',
                                        fontSize: '18px', flexShrink: 0
                                    }}>
                                        {pay?.icon || conf.icon}
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#1A1A1A' }}>
                                            {tx.description}
                                        </div>
                                        <div style={{ fontSize: '12px', color: '#aaa', marginTop: '2px' }}>
                                            {formatDateTime(tx.date, {
                                                day: 'numeric', month: 'long',
                                                hour: '2-digit', minute: '2-digit'
                                            })}
                                            {pay && (
                                                <span style={{ marginLeft: '10px' }}>
                                                    {pay.label}
                                                </span>
                                            )}
                                            {tx.notes && (
                                                <span style={{ marginLeft: '10px' }}>
                                                    · {tx.notes}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div style={{
                                        fontWeight: '800', fontSize: '15px',
                                        color: tx.amount > 0 ? '#2e7d32' : tx.amount < 0 ? '#c62828' : '#888'
                                    }}>
                                        {tx.amount > 0 ? '+' : ''}{(tx.amount || 0).toLocaleString('ru-RU')} ₽
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

        </OwnerPageLayout>
    );
}